import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import api from './services/apiService';
import { authService } from './services/authService';
import { FaSearch, FaQuestionCircle, FaThumbsUp, FaThumbsDown } from 'react-icons/fa';
import './scss/Help.scss';

function Help() {
  const [categories, setCategories] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [expandedQuestions, setExpandedQuestions] = useState(new Set());
  const navigate = useNavigate();
  const userType = sessionStorage.getItem('user_type') || 'player';

  useEffect(() => {
    fetchHelpData();
  }, []);

  useEffect(() => {
    filterQuestions();
  }, [questions, selectedCategory, searchTerm]);

  const fetchHelpData = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await api.get(`${process.env.REACT_APP_API_URL}/get_help_data.php`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setCategories(response.data.categories);
        setQuestions(response.data.questions);
      }
    } catch (error) {
      console.error('Error fetching help data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterQuestions = () => {
    let filtered = questions;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(q => q.category_slug === selectedCategory);
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(q => 
        q.question.toLowerCase().includes(searchLower) ||
        q.answer.toLowerCase().includes(searchLower) ||
        (q.keywords && q.keywords.toLowerCase().includes(searchLower))
      );
    }

    setFilteredQuestions(filtered);
  };

  const toggleQuestion = (questionId) => {
    const newExpanded = new Set(expandedQuestions);
    if (newExpanded.has(questionId)) {
      newExpanded.delete(questionId);
    } else {
      newExpanded.add(questionId);
    }
    setExpandedQuestions(newExpanded);
  };

  const handleFeedback = async (questionId, isHelpful) => {
    try {
      const token = sessionStorage.getItem('token');
      await api.post(`${process.env.REACT_APP_API_URL}/submit_help_feedback.php`, {
        question_id: questionId,
        is_helpful: isHelpful
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      // Update the question's feedback counts locally
      setQuestions(prevQuestions => 
        prevQuestions.map(q => 
          q.question_id === questionId 
            ? { 
                ...q, 
                helpful_count: isHelpful ? q.helpful_count + 1 : q.helpful_count,
                not_helpful_count: !isHelpful ? q.not_helpful_count + 1 : q.not_helpful_count
              }
            : q
        )
      );
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  const getCategoryName = (slug) => {
    const category = categories.find(c => c.slug === slug);
    return category ? category.name : slug;
  };

  if (loading) {
    return (
      <div className="Help">
        <Header />
        <div className="container">
          <div className="loading">Loading help content...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="Help">
      <Header />
      <div className="container">
        <div className="help-header">
          <h1>Help & FAQ</h1>
          <p>Find answers to frequently asked questions about FirstWhistle</p>
        </div>

        {/* Search Bar */}
        <div className="search-section">
          <div className="search-container">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search for help topics..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        {/* Category Filter */}
        <div className="category-filter">
          <button
            className={`category-btn ${selectedCategory === 'all' ? 'active' : ''}`}
            onClick={() => setSelectedCategory('all')}
          >
            All Categories
          </button>
          {categories.map(category => (
            <button
              key={category.category_id}
              className={`category-btn ${selectedCategory === category.slug ? 'active' : ''}`}
              onClick={() => setSelectedCategory(category.slug)}
            >
              {category.name}
            </button>
          ))}
        </div>

        {/* Questions List */}
        <div className="questions-section">
          {filteredQuestions.length === 0 ? (
            <div className="no-results">
              <FaQuestionCircle />
              <h3>No questions found</h3>
              <p>Try adjusting your search terms or selecting a different category.</p>
            </div>
          ) : (
            <div className="questions-list">
              {filteredQuestions.map(question => (
                <div key={question.question_id} className="question-item">
                  <div 
                    className="question-header"
                    onClick={() => toggleQuestion(question.question_id)}
                  >
                    <h3 className="question-text">{question.question}</h3>
                    <div className="question-meta">
                      <span className="category-tag">{getCategoryName(question.category_slug)}</span>
                      <span className="expand-icon">
                        {expandedQuestions.has(question.question_id) ? '−' : '+'}
                      </span>
                    </div>
                  </div>
                  
                  {expandedQuestions.has(question.question_id) && (
                    <div className="question-content">
                      <div className="answer">{question.answer}</div>
                      
                      <div className="feedback-section">
                        <p>Was this helpful?</p>
                        <div className="feedback-buttons">
                          <button
                            className="feedback-btn helpful"
                            onClick={() => handleFeedback(question.question_id, true)}
                          >
                            <FaThumbsUp /> Yes ({question.helpful_count})
                          </button>
                          <button
                            className="feedback-btn not-helpful"
                            onClick={() => handleFeedback(question.question_id, false)}
                          >
                            <FaThumbsDown /> No ({question.not_helpful_count})
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Contact Section */}
        <div className="contact-section">
          <h2>Still need help?</h2>
          <p>Can't find what you're looking for? Contact our support team.</p>
          <a 
            href="mailto:support@firstwhistle.app"
            className="contact-btn"
          >
            Contact Support
          </a>
        </div>
      </div>
    </div>
  );
}

export default Help; 